import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { useUpdateCallStatusMutation } from "@/hooks/voice/useVoiceCallsQuery";

type VoiceCallStatus =
  | "open"
  | "action_required"
  | "in_progress"
  | "closed"
  | "archived";

const allVoiceCallStatuses: VoiceCallStatus[] = [
  "open",
  "action_required",
  "in_progress",
  "closed",
  "archived",
];

// Helper function to get status badge props (business workflow status)
const getStatusBadgeProps = (status: string | null | undefined) => {
  switch (status) {
    case "open":
      return {
        className:
          "capitalize text-xs bg-blue-100 text-blue-800 border-blue-200",
        variant: "outline" as const,
      };
    case "action_required":
      return {
        className: "capitalize text-xs bg-red-100 text-red-800 border-red-200",
        variant: "outline" as const,
      };
    case "in_progress":
      return {
        className:
          "capitalize text-xs bg-yellow-100 text-yellow-800 border-yellow-200",
        variant: "outline" as const,
      };
    case "closed":
      return {
        className:
          "capitalize text-xs bg-green-100 text-green-800 border-green-200",
        variant: "outline" as const,
      };
    case "archived":
      return {
        className:
          "capitalize text-xs bg-gray-100 text-gray-800 border-gray-200",
        variant: "outline" as const,
      };
    default:
      return {
        className: "capitalize text-xs border-gray-300 text-gray-600",
        variant: "outline" as const,
      };
  }
};

const getStatusLabel = (status: VoiceCallStatus): string => {
  switch (status) {
    case "open":
      return "Open";
    case "action_required":
      return "Action Required";
    case "in_progress":
      return "In Progress";
    case "closed":
      return "Closed";
    case "archived":
      return "Archived";
    default:
      return status;
  }
};

interface VoiceCallStatusDropdownProps {
  callId: string;
  currentStatus: VoiceCallStatus | null | undefined;
  onStatusChange?: (newStatus: VoiceCallStatus) => void;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  className?: string;
  defaultStatus?: VoiceCallStatus;
}

export function VoiceCallStatusDropdown({
  callId,
  currentStatus,
  onStatusChange,
  disabled = false,
  size = "md",
  className,
  defaultStatus = "open",
}: VoiceCallStatusDropdownProps) {
  const updateStatusMutation = useUpdateCallStatusMutation();

  const handleStatusChange = (newStatus: VoiceCallStatus) => {
    if (
      !callId ||
      newStatus === currentStatus ||
      updateStatusMutation.isPending
    ) {
      return;
    }

    updateStatusMutation.mutate(
      { callId, status: newStatus },
      {
        onSuccess: () => {
          // Call the callback if provided
          onStatusChange?.(newStatus);
        },
      },
    );
  };

  // If updating, show loading state
  if (updateStatusMutation.isPending) {
    return (
      <div className="inline-flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm text-gray-500">Updating...</span>
      </div>
    );
  }

  const statusProps = getStatusBadgeProps(currentStatus || defaultStatus);

  return (
    <Select
      value={currentStatus || defaultStatus}
      onValueChange={handleStatusChange}
      disabled={disabled}
    >
      <SelectTrigger
        className={`border-0 bg-transparent p-0 h-auto w-auto hover:opacity-75 transition-opacity ${className}`}
      >
        <Badge
          variant={statusProps.variant}
          className={`${statusProps.className} cursor-pointer`}
        >
          {(currentStatus || defaultStatus).replace("_", " ")}
        </Badge>
      </SelectTrigger>
      <SelectContent>
        {allVoiceCallStatuses.map((status) => {
          const statusBadgeProps = getStatusBadgeProps(status);
          return (
            <SelectItem
              key={status}
              value={status}
              className="hover:bg-slate-50 focus:bg-slate-50 data-[highlighted]:bg-slate-50"
            >
              <div className="flex items-center gap-2">
                <Badge
                  variant={statusBadgeProps.variant}
                  className={statusBadgeProps.className}
                >
                  {getStatusLabel(status)}
                </Badge>
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
